import { supabase } from "@/integrations/supabase/client";
import ExcelJS from "exceljs";

export async function exportEmployeesToCSV() {
  const { data, error } = await supabase
    .from("employees")
    .select("*")
    .is("archived_at", null)
    .order("name");

  if (error) throw error;

  // Note: Sensitive fields (tax_id, social_security_number, iban, bic, health_insurance_*) 
  // are now encrypted and stored in employee_sensitive_data table.
  // Export only shows masked placeholders for security.
  const exportData = data?.map((emp) => ({
    "Personalnummer": emp.employee_number || "",
    "Name": emp.name,
    "Position": emp.position,
    "Abteilung": emp.department || "",
    "Geschlecht": emp.gender || "",
    "Geburtsdatum": emp.birth_date || "",
    "Nationalität": emp.nationality || "",
    "Straße": emp.address_street || "",
    "PLZ": emp.address_zip || "",
    "Stadt": emp.address_city || "",
    "Land": emp.address_country || "",
    "Telefon": emp.phone || "",
    "Mobil": emp.mobile || "",
    "Private E-Mail": emp.private_email || "",
    "Notfallkontakt Name": emp.emergency_contact_name || "",
    "Notfallkontakt Telefon": emp.emergency_contact_phone || "",
    "Steuer-ID": "[verschlüsselt]",
    "Sozialversicherungsnummer": "[verschlüsselt]",
    "Bank": emp.bank_name || "",
    "IBAN": "[verschlüsselt]",
    "BIC": "[verschlüsselt]",
    "Vertragsart": emp.contract_type || "",
    "Wochenstunden": emp.weekly_hours || "",
    "Urlaubstage": emp.vacation_days || "",
    "Eintrittsdatum": emp.employment_start_date || "",
    "Austrittsdatum": emp.employment_end_date || "",
    "Notizen": emp.notes || "",
  }));

  return exportData || [];
}

export async function exportSalaryHistoryToCSV() {
  const { data: salaries, error: salaryError } = await supabase
    .from("salary_history")
    .select(`
      *,
      employees!inner(name, employee_number)
    `)
    .order("effective_date", { ascending: false });

  if (salaryError) throw salaryError;

  const exportData = salaries?.map((sal) => ({
    "Personalnummer": sal.employees?.employee_number || "",
    "Mitarbeiter": sal.employees?.name || "",
    "Gültig ab": sal.effective_date,
    "Bruttogehalt": sal.base_salary,
    "Währung": sal.currency,
    "Gehaltsart": sal.salary_type,
    "Zahlungsfrequenz": sal.payment_frequency || "",
    "Steuerklasse": sal.tax_class || "",
    "Krankenversicherung": sal.health_insurance || "",
    "Rentenbeitrag": sal.pension_contribution || "",
    "Grund": sal.reason || "",
    "Notizen": sal.notes || "",
  }));

  return exportData || [];
}

export async function exportVehiclesToCSV() {
  const { data: vehicles, error } = await supabase
    .from("vehicles")
    .select(`
      *,
      employees:responsible_employee_id(name, employee_number)
    `)
    .order("name");

  if (error) throw error;

  const exportData = vehicles?.map((v) => ({
    "Fahrzeugname": v.name,
    "Hersteller": v.manufacturer || "",
    "Fahrzeugtyp": v.vehicle_type || "",
    "Kennzeichen": v.license_plate || "",
    "FIN": v.vin || "",
    "Baujahr": v.year_of_manufacture || "",
    "Erstzulassung": v.first_registration || "",
    "TÜV bis": v.tuv_until || "",
    "Eigentum": v.ownership || "",
    "Leasing bis": v.leased_until || "",
    "Leasing Rate": v.leasing_rate || "",
    "Versicherung/Monat": v.insurance_monthly || "",
    "Steuer/Jahr": v.tax_yearly || "",
    "Kraftstoff/Monat": v.fuel_monthly || "",
    "DKV-Karte": v.dkv_card || "",
    "Winterreifen": v.winter_tires ? "Ja" : "Nein",
    "Verantwortlicher": v.employees?.name || "",
    "Verantwortlicher Personalnr.": v.employees?.employee_number || "",
    "Notizen": v.notes || "",
  }));

  return exportData || [];
}

export async function exportVehicleCostsToCSV() {
  const { data: costs, error } = await supabase
    .from("vehicle_costs")
    .select(`
      *,
      employees!inner(name, employee_number),
      vehicles(name, license_plate)
    `)
    .order("date", { ascending: false });

  if (error) throw error;

  const exportData = costs?.map((c) => ({
    "Datum": c.date,
    "Kostenart": c.cost_type,
    "Betrag": c.amount,
    "Währung": c.currency,
    "Fahrzeug": c.vehicles?.name || c.vehicle_model || "",
    "Kennzeichen": c.vehicles?.license_plate || c.license_plate || "",
    "Mitarbeiter": c.employees?.name || "",
    "Personalnummer": c.employees?.employee_number || "",
    "Beschreibung": c.description || "",
    "Belegnummer": c.receipt_number || "",
    "Notizen": c.notes || "",
  }));

  return exportData || [];
}

export async function exportBonusHistoryToCSV() {
  const { data: bonuses, error } = await supabase
    .from("bonus_history")
    .select(`
      *,
      employees!inner(name, employee_number)
    `)
    .order("bonus_date", { ascending: false });

  if (error) throw error;

  const exportData = bonuses?.map((b) => ({
    "Personalnummer": b.employees?.employee_number || "",
    "Mitarbeiter": b.employees?.name || "",
    "Datum": b.bonus_date,
    "Bonusart": b.bonus_type,
    "Betrag": b.amount,
    "Währung": b.currency,
    "Beschreibung": b.description || "",
    "Zeitraum von": b.period_start || "",
    "Zeitraum bis": b.period_end || "",
    "Zielerreichung %": b.target_achieved || "",
    "Auszahlung": b.paid_date || "",
    "Notizen": b.notes || "",
  }));

  return exportData || [];
}

export async function exportBenefitsHistoryToCSV() {
  const { data: benefits, error } = await supabase
    .from("benefits_history")
    .select(`
      *,
      employees!inner(name, employee_number)
    `)
    .order("start_date", { ascending: false });

  if (error) throw error;

  const exportData = benefits?.map((b) => ({
    "Personalnummer": b.employees?.employee_number || "",
    "Mitarbeiter": b.employees?.name || "",
    "Leistungsart": b.benefit_type,
    "Leistungsname": b.benefit_name,
    "Anbieter": b.provider || "",
    "Start": b.start_date,
    "Ende": b.end_date || "",
    "Monatlicher Wert": b.monthly_value || "",
    "Währung": b.currency,
    "Status": b.status,
    "Zahlungsfrequenz": b.payment_frequency || "",
    "Notizen": b.notes || "",
  }));

  return exportData || [];
}

export async function downloadAsExcel(data: Record<string, unknown>[], filename: string) {
  if (!data || data.length === 0) {
    throw new Error("Keine Daten zum Exportieren vorhanden");
  }

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Daten");

  // Add headers
  const headers = Object.keys(data[0]);
  worksheet.addRow(headers);

  // Style header row
  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFE0E0E0" },
    };
  });

  // Add data rows
  data.forEach((row) => {
    const values = headers.map((header) => {
      const value = row[header];
      return value !== null && value !== undefined ? value : "";
    });
    worksheet.addRow(values);
  });

  // Auto-size columns
  worksheet.columns.forEach((column) => {
    let maxLength = 10;
    column.eachCell?.({ includeEmpty: true }, (cell) => {
      const cellValue = cell.value ? String(cell.value) : "";
      maxLength = Math.max(maxLength, cellValue.length);
    });
    column.width = Math.min(maxLength + 2, 50);
  });

  // Generate buffer and download
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { 
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" 
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${filename}.xlsx`;
  link.click();
  URL.revokeObjectURL(url);
}

export function downloadAsCSV(data: Record<string, unknown>[], filename: string) {
  if (!data || data.length === 0) {
    throw new Error("Keine Daten zum Exportieren vorhanden");
  }

  const headers = Object.keys(data[0]);
  
  // Escape CSV values properly
  const escapeCSV = (value: unknown): string => {
    const str = value !== null && value !== undefined ? String(value) : "";
    // If contains semicolon, quotes, or newlines, wrap in quotes and escape quotes
    if (str.includes(";") || str.includes('"') || str.includes("\n")) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  // Build CSV content
  const csvLines: string[] = [];
  csvLines.push(headers.map(escapeCSV).join(";"));
  
  data.forEach((row) => {
    const values = headers.map((header) => escapeCSV(row[header]));
    csvLines.push(values.join(";"));
  });

  const csv = csvLines.join("\n");
  
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${filename}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}
